import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AccountSmtpsService } from '../../../core/services/account-smtps/account-smtps.service';
import { AlertService } from '../../../core/services/alert/alert.service';

@Component({
  selector: 'app-form-add-smtp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './form-add-smtp.component.html',
  styleUrl: './form-add-smtp.component.css',
})
export class FormAddSmtpComponent implements OnChanges {
  @Output() backButtonClick = new EventEmitter<boolean>();
  @Output() addSmtp = new EventEmitter<boolean>();
  @Input() smtpData: any = null;

  form!: FormGroup;
  edit = false;
  reference: string | null = null;

  constructor(
    private fb: FormBuilder,
    private accountSmtpsService: AccountSmtpsService,
    private alertService: AlertService,
  ) {
    this.initForm();
  }

  initForm(): void {
    this.form = this.fb.group({
      label: ['', Validators.required],
      host: ['', Validators.required],
      port: [587, [Validators.required, Validators.min(1), Validators.max(65535)]],
      from_address: ['', [Validators.required, Validators.email]],
      password: [''],
      encryption: ['tls'],
      username: [''],
      from_name: [''],
      is_default: [false],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['smtpData']) {
      this.loadData();
    }
  }

  loadData(): void {
    if (this.smtpData) {
      this.edit = true;
      this.reference = this.smtpData.reference;
      this.form.patchValue({
        label: this.smtpData.label || '',
        host: this.smtpData.host || '',
        port: this.smtpData.port || 587,
        from_address: this.smtpData.from_address || '',
        password: '',  // Always empty - write-only
        encryption: this.smtpData.encryption || 'tls',
        username: this.smtpData.username || '',
        from_name: this.smtpData.from_name || '',
        is_default: this.smtpData.is_default || false,
      });
    } else {
      this.edit = false;
      this.reference = null;
      this.form.reset({
        label: '',
        host: '',
        port: 587,
        from_address: '',
        password: '',
        encryption: 'tls',
        username: '',
        from_name: '',
        is_default: false,
      });
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control?.invalid && control?.touched);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.alertService.loading('swal_messages.please_wait');
    const formValue = { ...this.form.value };

    // Don't send password if empty (keep existing on update)
    if (!formValue.password) {
      if (this.edit) {
        delete formValue.password;
      }
      // On create, password is required - validation handles it
    }

    // Clean nullish encryption
    if (formValue.encryption === 'null' || formValue.encryption === '') {
      formValue.encryption = null;
    }

    if (this.edit && this.reference) {
      this.accountSmtpsService.updateSmtp(this.reference, formValue).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.account_smtps.account_smtp_updated');
          this.addSmtp.emit(true);
          this.emitBack();
        },
        error: (error) => this.alertService.error(error.error),
      });
    } else {
      // Password required on create
      if (!formValue.password) {
        this.alertService.close();
        this.form.get('password')?.setErrors({ required: true });
        this.form.get('password')?.markAsTouched();
        return;
      }
      this.accountSmtpsService.createSmtp(formValue).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.account_smtps.account_smtp_created');
          this.addSmtp.emit(true);
          this.emitBack();
        },
        error: (error) => this.alertService.error(error.error),
      });
    }
  }

  emitBack(): void {
    this.backButtonClick.emit(true);
  }
}
